import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  Inject,
  inject,
  OnInit
} from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";

import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { Store } from "@ngxs/store";
import { JsonSchemaState, Schema } from "@vp/data-access/json-schema";
import { OrganizationState } from "@vp/data-access/organization";
import { UiSchemaState } from "@vp/data-access/ui-schema";
import {
  UiSchemaCollection,
  UiSchemaConfigService,
  UiSchemaLayoutProvider
} from "@vp/formly/ui-schema-config";
import { TagType } from "@vp/models";
import { filterNullMap } from "@vp/shared/operators";
import { mergeDeep } from "@vp/shared/utilities";
import { JSONSchema7 } from "json-schema";
import { BehaviorSubject, combineLatest, Observable } from "rxjs";
import { concatMap, filter, map } from "rxjs/operators";

export interface DialogData {
  tagTypeId: string;
}

@Component({
  selector: "vp-web-app-add-tag-dialog",
  standalone: false,
  templateUrl: "./add-tag-dialog.component.html",
  styleUrls: ["./add-tag-dialog.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AddTagDialogComponent implements OnInit {
  tagTypes$: Observable<TagType[]> = inject(Store).select(OrganizationState.tagTypes);
  schemas$: Observable<Schema[]> = inject(Store).select(JsonSchemaState.schemas);
  uiSchemas$: Observable<UiSchemaCollection[]> = inject(Store).select(UiSchemaState.uiSchemas);

  formGroup = new UntypedFormGroup({});
  options: FormlyFormOptions = {
    formState: { disabled: false }
  };
  fields: FormlyFieldConfig[] = [];

  private readonly ready$$ = new BehaviorSubject<boolean>(false);
  private readonly model$$ = new BehaviorSubject<Record<string, unknown>>({});

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private readonly dialogRef: MatDialogRef<AddTagDialogComponent>,
    private readonly uiSchemaConfigService: UiSchemaConfigService,
    private readonly uiSchemaLayoutProvider: UiSchemaLayoutProvider,
    readonly formBuilder: UntypedFormBuilder
  ) {}

  ready$ = this.ready$$.pipe(filter(ready => ready));
  model$ = this.model$$.asObservable();

  @HostListener("window:keyup.esc") onKeyUp() {
    this.close(false);
  }

  @HostListener("window:beforeunload", ["$event"]) unloadHandler(event: Event) {
    event.returnValue = false;
  }

  ngOnInit(): void {
    combineLatest([
      this.tagTypes$.pipe(
        map(tagTypes => tagTypes.find(tt => tt.tagTypeId === this.data.tagTypeId)),
        filterNullMap()
      ),
      this.schemas$.pipe(
        map(schemas => schemas.find(s => s.name === "tags")),
        filterNullMap(),
        map((schema: Schema) => schema.schema)
      ),
      this.uiSchemas$.pipe(map(uiSchemas => uiSchemas.find(c => c.scope === "tags") ?? null))
    ])
      .pipe(
        concatMap(
          ([tagType, schema, uiSchema]: [TagType, JSONSchema7, UiSchemaCollection | null]) => {
            const layout: UiSchemaCollection = uiSchema
              ? mergeDeep(uiSchema, tagType?.tagLayout, "concat")
              : tagType?.tagLayout;

            if (uiSchema) {
              this.uiSchemaConfigService.addScopedConfig(layout, "add-tags", "replace");
            }

            return this.uiSchemaLayoutProvider.applyScopes("add-tags", schema, "add-tags");
          }
        )
      )
      .subscribe(jsonSchema => {
        this.fields = this.uiSchemaLayoutProvider.getFieldConfig(jsonSchema);
        this.ready$$.next(true);
      });
  }

  onCancel = () => {
    this.close(false);
  };

  onSave = () => {
    this.close(true);
  };

  close(save: boolean) {
    if (save && this.formGroup.valid) {
      this.dialogRef.close(this.formGroup.getRawValue());
    } else {
      if (this.formGroup.pristine || this.formGroup.invalid) {
        this.dialogRef.close();
      } else {
        if (confirm("Are you sure you want to cancel and potentially lose data?")) {
          this.dialogRef.close();
        }
      }
    }
  }
}
